<template>
	<div class="permission-page">
		<div class="header">
			<div class="header-top">
				<div>
					<h1>{{ $t("permissionPage.title") }}</h1>
					<p>{{ $t("permissionPage.description") }}</p>
				</div>
				<Button v-if="!loading && !error" variant="primary" size="m" @click="openAddModal">
					{{ $t("permissionPage.addPermission") }}
				</Button>
			</div>
			<router-link to="/" class="back-link">{{ $t("permissionPage.backToHome") }}</router-link>
		</div>

		<AddPermissionModal
			:is-open="addModalOpen"
			:saving="addModalSaving"
			:submit-error="addModalError"
			@close="closeAddModal"
			@save="handleCreatePermission"
		/>

		<div v-if="loading" class="loading">
			{{ $t("permissionPage.loading") }}
		</div>

		<div v-else-if="error" class="error">
			{{ error }}
		</div>

		<div v-else class="permission-grid">
			<div v-for="role in roles" :key="role.id" class="role-row">
				<div class="role-info">
					<h3>{{ role.name }}</h3>
					<p v-if="role.description">{{ role.description }}</p>
				</div>

				<div class="permissions-list">
					<div v-for="permission in allPermissions" :key="permission.id" class="permission-item">
						<label class="permission-checkbox">
							<input
								type="checkbox"
								:checked="isPermissionAssigned(role, permission.id)"
								@change="handlePermissionChange(role.id, permission.id, ($event.target as HTMLInputElement).checked)"
								:disabled="updating"
							/>
							<span class="permission-name">{{ permission.name }}</span>
							<span v-if="permission.description" class="permission-description"> - {{ permission.description }} </span>
						</label>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import { useRouter } from "vue-router"
import { Permission, UseRootStore } from "@/model/RootStore"
import AddPermissionModal from "@/components/admin/AddPermissionModal.vue"
import Button from "@/components/ui/Button.vue"
import { getRolesWithPermissions, getAllPermissions, assignPermissionToRole, createPermission } from "@/utils/ApiClient"
import { useToastStore } from "@/stores/toastStore.ts"

const router = useRouter()
const store = UseRootStore()
const toastStore = useToastStore()

const loading = ref(true)
const error = ref("")
const updating = ref(false)
const roles = ref<any[]>([])
const allPermissions = ref<any[]>([])

const addModalOpen = ref(false)
const addModalSaving = ref(false)
const addModalError = ref("")

const openAddModal = () => {
	addModalError.value = ""
	addModalOpen.value = true
}

const closeAddModal = () => {
	addModalOpen.value = false
	addModalError.value = ""
}

const handleCreatePermission = async ({ name, description }: { name: string; description: string }) => {
	try {
		addModalSaving.value = true
		addModalError.value = ""
		const created = await createPermission(store.userApiToken, name, description)
		allPermissions.value = [...allPermissions.value, created].sort((a: any, b: any) => a.name.localeCompare(b.name))
		closeAddModal()
		toastStore.success("permissionPage.createSuccess")
	} catch (err) {
		addModalError.value = err instanceof Error ? err.message : "Failed to create permission"
	} finally {
		addModalSaving.value = false
	}
}

const loadData = async () => {
	try {
		loading.value = true
		error.value = ""

		const [rolesData, permissionsData] = await Promise.all([
			getRolesWithPermissions(store.userApiToken),
			getAllPermissions(store.userApiToken),
		])

		roles.value = rolesData
		allPermissions.value = permissionsData.sort((a: any, b: any) => a.name.localeCompare(b.name))
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Failed to load data"
	} finally {
		loading.value = false
	}
}

const isPermissionAssigned = (role: any, permissionId: string): boolean => {
	return role.permissions?.some((p: any) => p.id === permissionId)
}

const handlePermissionChange = async (roleId: string, permissionId: string, checked: boolean) => {
	try {
		updating.value = true

		const role = roles.value.find(r => r.id === roleId)
		if (!role) return

		const currentPermissionIds = role.permissions?.map((p: any) => p.id) || []

		// Update the permission list
		let newPermissionIds: string[]
		if (checked) {
			newPermissionIds = [...currentPermissionIds, permissionId]
		} else {
			newPermissionIds = currentPermissionIds.filter((id: string) => id !== permissionId)
		}

		// API now returns the updated role
		const updatedRole = await assignPermissionToRole(
			store.userApiToken,
			Number(roleId),
			newPermissionIds.map(id => Number(id))
		)

		// Simply replace the role in the array
		const roleIndex = roles.value.findIndex(r => r.id === roleId)
		if (roleIndex !== -1) {
			roles.value[roleIndex] = updatedRole
		}
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Failed to update permissions"
	} finally {
		updating.value = false
	}
}

onMounted(async () => {
	if (!store.currentUser?.has(Permission.AuthorizationWrite)) {
		router.push("/")
		return
	}

	await loadData()
})
</script>

<style scoped>
:deep(body) {
	overflow-y: auto !important;
	height: auto !important;
	min-height: 100vh !important;
}

:deep(html) {
	overflow-y: auto !important;
	height: auto !important;
}

:deep(#app) {
	overflow-y: auto !important;
	height: auto !important;
	min-height: 100vh !important;
}
.permission-page {
	max-width: 1600px;
	margin: 0 auto;
	padding: 2rem;
	height: 100vh;
	overflow-y: auto;
	position: relative;
}

.header {
	margin-bottom: 2rem;
}

.header-top {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 0.5rem;
}

.header h1 {
	margin: 0 0 0.5rem 0;
	color: #333;
}

.header p {
	margin: 0 0 1rem 0;
	color: #666;
}

.back-link {
	display: inline-block;
	color: #007bff;
	text-decoration: none;
	margin-top: 1rem;
}

.back-link:hover {
	text-decoration: underline;
}

.loading,
.error {
	text-align: center;
	padding: 2rem;
	font-size: 1.1rem;
}

.error {
	color: #dc3545;
}

.permission-grid {
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	overflow: visible;
	max-height: none;
	height: auto;
	min-height: 0;
}

.role-row {
	border-bottom: 1px solid #dee2e6;
	padding: 1.5rem;
}

.role-row:last-child {
	border-bottom: none;
}

.role-info {
	text-align: center;
}

.role-info h3 {
	margin: 0 0 0.5rem 0;
	color: #333;
	font-size: 1.5rem;
	text-transform: capitalize;
}

.role-info p {
	margin: 0;
	color: #666;
	font-size: 0.9rem;
}

.permissions-list {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.75rem;
	max-height: none;
	overflow: visible;
	margin-top: 1rem;
}

.permission-item {
	display: flex;
	align-items: center;
}

.permission-checkbox {
	display: flex;
	align-items: center;
	cursor: pointer;
	font-size: 1rem;
	color: #333;
}

.permission-checkbox input[type="checkbox"] {
	margin-right: 0.5rem;
	cursor: pointer;
}

.permission-name {
	font-weight: 500;
	font-size: 1rem;
}

.permission-description {
	color: #666;
	font-weight: normal;
	font-size: 0.75rem;
}

.permission-checkbox input[type="checkbox"]:disabled {
	cursor: not-allowed;
	opacity: 0.6;
}
</style>
